import { Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Box } from '@mui/material';

import DialogsManagement from 'components/sections/Dialogs';
import Footer from 'components/sections/Footer';
import Navigation from 'components/sections/Navigation';

import useBanners from 'store/banners';
import useCategories from 'store/categories';
import useCountries from 'store/countries';
import useCurrentUser from 'store/currentUser';
import useFavoritesAndRecents from 'store/favorites';
import useGlobalSettings from 'store/globalSettings';
import useSelectedLanguage from 'store/language';
import useLanguagesList from 'store/languagesList';
import useSettings, { IWebsiteConfigs } from 'store/settings';
import useWebsiteSports from 'store/websiteSports';
import useSelectedTheme from 'store/theme';
import { themeStorage } from 'store/storage';

import useScreen from 'hooks/useScreen';
import useSearchParamsTriggers from 'hooks/useSearchParamsTriggers';
import useSockets from 'hooks/useSockets';

import useDomainSettings from 'store/domains';
import useWebsiteContent from 'store/websiteContent';
import { USER_LOGOUT } from 'utils/logoutListener';
import { SettingsService } from 'api/settings';
import AppLoading from './AppLoading';
import Metatags from './Metatags';
import SwitchRouter from './SwitchRouter';
import appPages from './routes';
import useMount from 'hooks/useMount';
import useDynamicManifest from 'hooks/useDynamicManifest';
import useNativeNotifications from 'utils/messaging/notifications/useNativeNotification';

const PagesLayout = (): JSX.Element => {
  const [isAppLoading, setIsAppLoading] = useState(true);

  const deferredPrompt = useRef(null);
  const { isMobile } = useScreen();
  const socketActions = useSockets();
  const { pathname } = useLocation();
  const [, bannersActions] = useBanners();
  const [params] = useSearchParams();
  const [
    {
      data: { meta = {} as IWebsiteConfigs },
    },
    settingsActions,
  ] = useSettings();

  const [, countriesActions] = useCountries();
  const [, websiteContentActions] = useWebsiteContent();
  const [, favoritesActions] = useFavoritesAndRecents();
  const [, globalSettingsActions] = useGlobalSettings();
  const [, categoriesActions] = useCategories();
  const [, websiteSportsActions] = useWebsiteSports();
  const [, languagesActions] = useLanguagesList();
  const [currentUser, currentUserActions] = useCurrentUser();
  const [selectedLanguage] = useSelectedLanguage();
  const [_, domainActions] = useDomainSettings();
  const { enableNotifications } = useNativeNotifications();
  const { generated } = useDynamicManifest();
  const [, themeActions] = useSelectedTheme();

  useSearchParamsTriggers();

  useMount(() => {
    (async () => {
      await languagesActions.read();
      countriesActions.read();
      currentUserActions.read();
      globalSettingsActions.read();
      websiteSportsActions.read();
      domainActions.read();
      websiteContentActions.read();
      settingsActions.read().finally(() => {
        setIsAppLoading(false);
      });
      if (params.get('reference')) {
        SettingsService.trackVisit(
          params.get('reference'),
          localStorage.getItem('reference') !== params.get('reference')
        ).catch((error) => console.error(error));
        localStorage.setItem('reference', params.get('reference'));
      }
    })();
  });

  useEffect(() => {
    if (selectedLanguage?.id) {
      bannersActions.read();
      categoriesActions.read();
      websiteContentActions.read();
    }
  }, [selectedLanguage?.id]);

  useEffect(() => {
    if (currentUser?.data?.id) {
      socketActions.connect();
      favoritesActions.read();
      enableNotifications();
    }
  }, [currentUser?.data?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useMount(() => {
    const beforeInstallPromptHandler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;

      if (meta.showPWAInstall && generated) {
        deferredPrompt.current.prompt();
      }
    };
    window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler);
    };
  });

  useEffect(() => {
    if (meta.showPWAInstall && generated) {
      deferredPrompt.current?.prompt();
    }
  }, [meta.showPWAInstall]);

  useEffect(() => {
    if (meta.defaultLight && !themeStorage.get()) {
      themeActions.set('light');
    }
  }, [meta.defaultLight]);

  useMount(() => {
    const logoutListener = () => {
      currentUserActions.clear();
    };
    window.addEventListener(USER_LOGOUT, logoutListener);
    return () => window.removeEventListener(USER_LOGOUT, logoutListener);
  });

  const isSportSelected = isMobile && pathname === '/sport';
  const showFooter =
    !pathname.includes('/game/') &&
    !pathname.includes('/sign-in') &&
    !pathname.includes('/sign-up') &&
    !pathname.includes('/forgot-password') &&
      (!isSportSelected && (isMobile ? pathname === '/' : true) && meta?.isHomepageActive);


  return (
    <>
      {isAppLoading && <AppLoading />}
      <Box sx={{ marginBottom: 6 }}>
        <Metatags />
        <Navigation />
        <DialogsManagement />
        <Suspense fallback={<AppLoading />}>
          <SwitchRouter routes={appPages} />
        </Suspense>
      </Box>
      {showFooter && <Footer />}
    </>
  );
};

export default PagesLayout;
